import React, { useState, useEffect, useRef, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";

// API calls are centralized at the Page level. Do not import getPlalionSensorData/getOpenWeatherData here to avoid double calls.
import AtomsGaugeInfo from "../atoms/AtomsGaugeInfo";
import MoleculeMenuBoardInfoBoard from "../molecules/MoleculeMenuBoardInfoBoard";
import MoleculeLinkContentInfoBoard from "../molecules/MoleculeLinkContentInfoBoard";
import MoleculeGeneralContentInfoBoard from "../molecules/MoleculeGeneralContentInfoBoard";
import { useAtom } from "jotai";
import {
  AUTODISPLAY_REACT_STATE,
  AUTODISPLAY_DISPLAY_WIDTH,
  AUTODISPLAY_DISPLAY_HEIGHT,
} from "../../../AtomStates";

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% - 4em));
  }
`;

// 테마별 컬러 매핑 객체
const themeColors = {
  dark: {
    mainColor: "white",
    MainContainerBackgroundLinearGradient01: "rgba(0, 0, 0, 0.4)",
    MainContainerBackgroundLinearGradient02: "rgba(0, 0, 0, 0.4)",
    MainPanelBackgroundLinearGradient01: "rgba(0, 0, 0, 0.3)",
    MainPanelBackgroundLinearGradient02: "rgba(0, 0, 0, 0.5)",
    MainPanelBackgroundLinearGradient03: "rgba(0, 0, 0, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherPanelBackgroundLinearGradient01: "rgba(0, 0, 0, 0.3)",
    WeatherPanelBackgroundLinearGradient02: "rgba(0, 0, 0, 0.5)",
    WeatherPanelBackgroundLinearGradient03: "rgba(0, 0, 0, 0.3)",
    WeatherPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherLabelColor: "rgba(255, 255, 255, 0.6)",
    bottomMessageBackground: "rgba(255, 255, 255, 0.2)",
  },
  light: {
    mainColor: "black",
    MainContainerBackgroundLinearGradient01: "rgba(255, 255, 255, 0.4)",
    MainContainerBackgroundLinearGradient02: "rgba(255, 255, 255, 0.4)",
    MainPanelBackgroundLinearGradient01: "rgba(255, 255, 255, 0.3)",
    MainPanelBackgroundLinearGradient02: "rgba(255, 255, 255, 0.5)",
    MainPanelBackgroundLinearGradient03: "rgba(255, 255, 255, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherPanelBackgroundLinearGradient01: "rgba(255, 255, 255, 0.3)",
    WeatherPanelBackgroundLinearGradient02: "rgba(255, 255, 255, 0.5)",
    WeatherPanelBackgroundLinearGradient03: "rgba(255, 255, 255, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherLabelColor: "rgba(0, 0, 0, 0.6)",
    bottomMessageBackground: "rgba(0, 0, 0, 0.2)",
  },
};

/**
 * 테마모드에 따른 색상을 가져오는 함수
 * @param {string} colorName - 색상 이름 (예: "mainColor", "MainPanelOutline")
 * @param {string} themeMode - 테마 모드 ("dark" 또는 "light")
 * @returns {string} 해당 테마의 색상 값
 */
const getThemeColor = (colorName, themeMode = "dark") => {
  const theme = themeColors[themeMode] || themeColors.dark;
  return theme[colorName] || themeColors.dark[colorName];
};

/**
 * 테마모드에 따른 날씨 아이콘을 가져오는 함수
 * @param {string} iconType - 아이콘 타입 ("windspeed", "precipitation", "humidity")
 * @param {string} themeMode - 테마 모드 ("dark" 또는 "light")
 * @returns {string} 해당 테마의 아이콘 경로
 */

// 배경 이미지는 사용하지 않음 (단색 배경 적용)

const OrganismDisplayViewLinkLotationInfoBoard = ({
  viewId,
  textData: initialTextData,
  sensorSerialData: initialSensorSerialData,
  locationData: initialLocationData,
  themeMode: initialThemeMode, // "dark" or "light"
  weatherData: initialWeatherData,
  sensorData: initialSensorData,
  backgroundImage,
  logo,
  contentsDesignConcept,
  onTogglePlay,

  // 식단 데이터
  isMeal,
  menuData,
  noticeText,
  showWeeklyView,

  // 일반 콘텐츠 데이터
  title,
  content,

  // 링크 콘텐츠 데이터
  isLink,
  linkUrl
}) => {
  // atom에서 해상도 및 반응형 모드 정보 가져오기
  const [reactState] = useAtom(AUTODISPLAY_REACT_STATE);
  const [displayWidth] = useAtom(AUTODISPLAY_DISPLAY_WIDTH);
  const [displayHeight] = useAtom(AUTODISPLAY_DISPLAY_HEIGHT);

  // 고정 모드 조건을 더 명확하게 체크
  const isFixedMode =
    reactState === "fixed" && displayWidth > 0 && displayHeight > 0;

  // 반응형 모드에서는 기존과 완전히 동일한 초기값 사용
  const [orientation, setOrientation] = useState("landscape");
  const [isWider, setIsWider] = useState(false);
  const [isTaller, setIsTaller] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const messageContainerRef = useRef(null);
  const messageTextRef = useRef(null);

  // 기존 props를 useState로 관리
  const [textData, setTextData] = useState(initialTextData || "Loading...");
  const [sensorSerialData, setSensorSerialData] = useState(
    initialSensorSerialData
  );
  const [locationData, setLocationData] = useState(initialLocationData);
  const [themeMode, setThemeMode] = useState(initialThemeMode || "light");

  const [weatherData, setWeatherData] = useState(initialWeatherData || {
    temperature: "-",
    wind: "-",
    precipitation: "-",
    humidity: "-",
    pty: "-", // 강수형태 추가
  });
  const [sensorData, setSensorData] = useState(initialSensorData || {
    ozone: "0",
    temperature: "23",
    humidity: "55",
    dust: "0",
    voc: "3",
    co2: "403",
  });

  // 예보 데이터 state 추가
  const [weatherForecastData, setWeatherForecastData] = useState(initialWeatherData || {
    maxTemperature: "-",
    minTemperature: "-",
    sky: "-",
  });
  const [lastTempUpdateDate, setLastTempUpdateDate] = useState(null); // TMX, TMN 마지막 업데이트 날짜
  // Weather-minute fetch ref removed: Page handles weather fetches

  const month = currentDate.getMonth() + 1;
  const day = currentDate.getDate();
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");

  // props 동기화: 상위에서 콘텐츠가 변경되면 내부 상태 갱신
  useEffect(() => {
    setTextData(initialTextData || "");
  }, [initialTextData]);

  useEffect(() => {
    setSensorSerialData(initialSensorSerialData || null);
  }, [initialSensorSerialData]);

  useEffect(() => {
    if (initialSensorData) setSensorData(initialSensorData);
  }, [initialSensorData]);

  useEffect(() => {
    setLocationData(initialLocationData || null);
  }, [initialLocationData]);

  useEffect(() => {
    setThemeMode(initialThemeMode || "light");
  }, [initialThemeMode]);

  useEffect(() => {
    if (initialWeatherData) {
      setWeatherData(initialWeatherData);
      setWeatherForecastData((prev) => ({
        ...prev,
        minTemperature: initialWeatherData.minTemperature || prev.minTemperature,
        maxTemperature: initialWeatherData.maxTemperature || prev.maxTemperature,
        sky: initialWeatherData.sky || prev.sky,
        weatherIcon: initialWeatherData.weatherIcon || prev.weatherIcon,
        weatherMain: initialWeatherData.weatherMain || prev.weatherMain,
        pty: initialWeatherData.pty || prev.pty,
      }));
    }
  }, [initialWeatherData]);

  useEffect(() => {
    const checkOverflow = () => {
      const container = messageContainerRef.current;
      const text = messageTextRef.current;
      if (container && text) {
        const isOverflow = text.scrollWidth > container.clientWidth;
        if (isOverflow !== isTextOverflowing) {
          setIsTextOverflowing(isOverflow);
        }
      }
    };

    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [textData, orientation, isWider, isTaller, isTextOverflowing]);

  // Internal fetchSensorData removed: Page provides sensorData via props

  // 현재 온도와 최대/최저 온도 비교하여 업데이트
  useEffect(() => {
    if (
      weatherData.temperature &&
      (weatherForecastData.maxTemperature || weatherForecastData.minTemperature)
    ) {
      const currentTemp = parseFloat(weatherData.temperature);
      let shouldUpdate = false;
      let updateData = {};

      // 최대 온도 체크
      if (weatherForecastData.maxTemperature) {
        const maxTemp = parseFloat(weatherForecastData.maxTemperature);
        if (currentTemp > maxTemp) {
          console.log(
            `현재 온도(${currentTemp}°C)가 최대 온도(${maxTemp}°C)보다 높아서 최대 온도를 업데이트합니다.`
          );
          updateData.maxTemperature = weatherData.temperature;
          shouldUpdate = true;
        }
      }

      // 최저 온도 체크
      if (weatherForecastData.minTemperature) {
        const minTemp = parseFloat(weatherForecastData.minTemperature);
        if (currentTemp < minTemp) {
          console.log(
            `현재 온도(${currentTemp}°C)가 최저 온도(${minTemp}°C)보다 낮아서 최저 온도를 업데이트합니다.`
          );
          updateData.minTemperature = weatherData.temperature;
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        setWeatherForecastData((prev) => ({
          ...prev,
          ...updateData,
        }));
      }
    }
  }, [
    weatherData.temperature,
    weatherForecastData.maxTemperature,
    weatherForecastData.minTemperature,
  ]);

  // OpenWeather API를 사용한 날씨 데이터 가져오기
  // Internal fetchOpenWeatherData removed: Page provides weatherData via props

  // requestWeatherUpdate removed: Page handles weather updates

  // 좌표가 실제로 바뀐 경우에만 초기 로딩
  const locationKey = useMemo(() => {
    const x = locationData?.selectedDistricts?.x;
    const y = locationData?.selectedDistricts?.y;
    return x && y ? `${x},${y}` : null;
  }, [locationData?.selectedDistricts?.x, locationData?.selectedDistricts?.y]);

  // lastFetchedLocationKeyRef and currentLocationKeyRef removed: Page manages location-based refresh

  // Latest location ref usage removed; Page handles location-based refresh

  // Location-based internal load removed: Page handles initial weather/sensor data load and refresh

  // 타이머는 한 번만 설정 (mount/unmount) — Only date timer is kept; Page handles weather/sensor updates
  useEffect(() => {
    const dateTimerId = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // 1분(60초)마다 시간 업데이트

    return () => {
      clearInterval(dateTimerId);
    };
  }, []);

  // 센서 polling and internal fetch removed: Page updates sensorData prop periodically

  // 고정 모드일 때만 실행되는 useEffect
  useEffect(() => {
    if (reactState === "fixed" && displayWidth > 0 && displayHeight > 0) {
      const fixedRatio = displayWidth / displayHeight;
      setOrientation(fixedRatio > 1 ? "landscape" : "portrait");
      setIsWider(fixedRatio > 16 / 9);
      setIsTaller(fixedRatio < 9 / 16);
    }
  }, [reactState, displayWidth, displayHeight]);

  // 반응형 모드일 때만 실행되는 useEffect (기존과 완전히 동일)
  useEffect(() => {
    // reactState가 'active'이거나 undefined일 때만 실행 (기존 동작)
    if (reactState === "active" || reactState === undefined) {
      const handleResize = () => {
        const currentRatio = window.innerWidth / window.innerHeight;
        setOrientation(currentRatio > 1 ? "landscape" : "portrait");
        setIsWider(currentRatio > 16 / 9);
        setIsTaller(currentRatio < 9 / 16);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []); // 기존과 완전히 동일한 빈 배열

  const textContent =
    textData || "실내 환경과 날씨 정보를 실시간으로 확인하세요.";

  return (
    <OuterWrapper>
      <MainContainer
        orientation={orientation}
        isFixedMode={isFixedMode}
        displayWidth={displayWidth}
        displayHeight={displayHeight}
        themeMode={themeMode}
        backgroundImage={backgroundImage}
      >
        <ContentWrapper orientation={orientation}>
        {/* 메인 패널 */}
        <MainPanel orientation={orientation} themeMode={themeMode}>
          {/* 헤더 및 센서 그리드 (동일 행에 배치) */}
          <HeaderSensorRow orientation={orientation}>
            {/* 헤더 */}
            <HeaderContainer orientation={orientation}>
              <TitleSection orientation={orientation}>
                <MainTitle orientation={orientation} themeMode={themeMode}>
                  {sensorSerialData?.title || "Auto Display"}
                </MainTitle>
              </TitleSection>
              <DateTimeSection orientation={orientation}>
                <DateContainer>
                  <DateRow>
                    <DateText orientation={orientation} themeMode={themeMode}>{month}월</DateText>
                    <DateText orientation={orientation} themeMode={themeMode}>{day}일</DateText>
                  </DateRow>
                  <TimeText orientation={orientation} themeMode={themeMode}>
                    {hours}:{minutes}
                  </TimeText>
                </DateContainer>
              </DateTimeSection>
            </HeaderContainer>

            {/* 센서 그리드 */}
            <SensorGrid orientation={orientation}>
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={
                    sensorData.temperature === "-" ? 0 : sensorData.temperature
                  }
                  unit="°C"
                  label="온도"
                  minValue={-10}
                  maxValue={100}
                  goodRange={[15, 32]}
                  normalRange={[-10, 14]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator orientation={orientation} themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={sensorData.ozone === "-" ? 0 : sensorData.ozone}
                  unit="ppm"
                  label="오존"
                  minValue={0}
                  maxValue={0.2}
                  goodRange={[0, 0.03]}
                  normalRange={[0.03, 0.06]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator orientation={orientation} themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={sensorData.humidity === "-" ? 0 : sensorData.humidity}
                  unit="%"
                  label="습도"
                  minValue={0}
                  maxValue={100}
                  goodRange={[40, 70]}
                  normalRange={[0, 39]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator orientation={orientation} themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={sensorData.dust === "-" ? 0 : sensorData.dust}
                  unit="㎍/㎥"
                  label="미세먼지"
                  minValue={0}
                  maxValue={150}
                  goodRange={[0, 30]}
                  normalRange={[31, 80]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator orientation={orientation} themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={sensorData.voc === "-" ? 0 : sensorData.voc}
                  unit="ppm"
                  label="VOC"
                  minValue={0}
                  maxValue={1000}
                  goodRange={[0, 200]}
                  normalRange={[201, 500]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator orientation={orientation} themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper orientation={orientation}>
                <AtomsGaugeInfo
                  value={sensorData.co2 === "-" ? 0 : sensorData.co2}
                  unit="ppm"
                  label="이산화탄소"
                  minValue={0}
                  maxValue={3000}
                  goodRange={[0, 800]}
                  normalRange={[801, 1500]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
          </SensorGrid>
          </HeaderSensorRow>
        </MainPanel>

        {/* 중앙 패널: 메뉴/링크/일반 콘텐츠 렌더링 */}
        <CenterPanel orientation={orientation} themeMode={themeMode}>
          <CenterPanelContentWrapper orientation={orientation}>
            {/* isMeal -> 메뉴보드, isLink -> 링크 컴포넌트, 그 외 -> 일반 콘텐츠 컴포넌트 */}
            {isMeal ? (
              <MoleculeMenuBoardInfoBoard
                menuData={menuData}
                logo={logo}
                showWeeklyView={showWeeklyView}
                orientation={orientation}
              />
            ) : isLink ? (
              <MoleculeLinkContentInfoBoard
                contentsDesignConcept={contentsDesignConcept}
                title={title}
                content={content}
                weatherData={weatherData}
                linkUrl={linkUrl}
                logo={logo}
                orientation={orientation}
                // onTogglePlay={onTogglePlay}
              />
            ) : (
              <MoleculeGeneralContentInfoBoard
                contentsDesignConcept={contentsDesignConcept}
                title={title}
                content={content}
                weatherData={weatherData}
                logo={logo}
                orientation={orientation}
              />
            )}
          </CenterPanelContentWrapper>
        </CenterPanel>

        {/* 하단 메시지를 다시 독립적으로 */}
          {/* <BottomMessage ref={messageContainerRef} themeMode={themeMode} orientation={orientation}>
          <MessageTextContainer>
            <MessageText
              ref={messageTextRef}
              isOverflowing={isTextOverflowing}
              data-text={textContent}
              orientation={orientation}
            >
              {textContent}
            </MessageText>
          </MessageTextContainer>
        </BottomMessage> */}
        </ContentWrapper>
      </MainContainer>
    </OuterWrapper>
  );
};

export default OrganismDisplayViewLinkLotationInfoBoard;

// Styled Components
const OuterWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
`;

const MainContainer = styled.div`
  width: ${({ orientation }) =>
    orientation === "landscape" ? "1920px" : "1080px"};
  height: ${({ orientation }) =>
    orientation === "landscape" ? "1080px" : "1920px"};
  flex-shrink: 0;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ backgroundImage }) =>
    !backgroundImage || backgroundImage === "none"
      ? "transparent"
      : backgroundImage
      ? `url(${backgroundImage}) center center`
      : "transparent"};
  background-size: cover;
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-family: Pretendard, Arial, sans-serif;
  box-sizing: border-box;

  padding: 48px;

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    padding: 16px 16px;
    /* overflow-y: auto; */
    /* overflow-x: hidden; */
    align-items: flex-start;
  `
      : `
    overflow: hidden;
  `}
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px; /* 적당한 간격으로 복구 */
  min-height: 0; /* flex 컨테이너 shrink 허용 */

  --font-scaler: 19.2px;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    gap: 24px;
  `}
`;

const MainPanel = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  padding: 20px 10px; 
  background: linear-gradient(
    136deg,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient01", themeMode)}
      0%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient02", themeMode)}
      50%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient03", themeMode)}
      100%
  );
  border-radius: 20px;
  margin: 10px;
  outline: 3px
    ${({ themeMode }) => getThemeColor("MainPanelOutline", themeMode)} solid; // color name = MainPanelOutline
  outline-offset: -3px;
  backdrop-filter: blur(12px);
  min-height: auto;
  overflow: visible; /* hidden -> visible로 변경 */
  flex-shrink: 1; /* shrink 허용 */

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    padding: 15px;
    min-height: 230px;
    border-radius: 20px;
    margin: 5px;    
  `}
`;

const HeaderContainer = styled.div`
  align-self: stretch;
  justify-content: center;
  align-items: center;
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0; /* 헤더가 줄어들지 않도록 */
  min-height: 115.2px; /* 최소 높이 설정 */
  margin-bottom: ${({ orientation }) =>
    orientation === "portrait" ? "24px" : "0"}; /* 기본 하단 마진 (landscape에서는 제거) */

  padding: 0; /* 패딩 제거 - 센서와 정렬 맞추기 */
  flex: 0 1 30%; /* 헤더는 최대 너비 32% 정도 차지 (landscape에서) */
  
  /* 가로 모드일 때 세로 배치(타이틀 아래 시간), 세로 모드일 때 가로 배치(중앙 타이틀, 우측 시간) */
  flex-direction: column;
  
  position: relative; /* 세로 모드에서 시간 절대 위치 배치를 위해 */

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    justify-content: center; /* 타이틀 중앙 정렬 */
    align-items: center;
    gap: 5px;
  `
      : `
    justify-content: center;
    align-items: center; /* flex-start -> center로 변경 */
    gap: 5px;
  `}
`;

const HeaderSensorRow = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  flex-direction: ${({ orientation }) =>
    orientation === "portrait" ? "column" : "row"};
  /* allow children to wrap for very small widths */
  flex-wrap: nowrap;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    flex: 1;
    justify-content: space-between;
    gap: 5px;
  `}
`;

const TitleSection = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: center; /* flex-start -> center로 변경 */
  gap: 9.6px;
  display: flex;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    padding: 5px 5px 0 5px;
    gap: 5px;
  `}
`;

const MainTitle = styled.h1`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.3 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    font-size: calc(3 * var(--font-scaler));
  `}
`;

const SensorGrid = styled.div`
  display: grid;
  width: 100%;
  height: auto;
  flex: 1 1 100%;
  padding: 0;
  justify-content: center;
  align-items: center;
  min-height: 0;
  flex-shrink: 1;
  min-width: 0; /* allow flex children to shrink properly */

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, auto);
    gap: 8px;
    justify-content: center;
    align-items: center;
    justify-items: center;
    margin: 0 auto;
    place-items: center;

    /* 2행 3열 배치 - 라인 없이 */
    > *:nth-child(1) { grid-column: 1; grid-row: 1; } /* 온도 */
    > *:nth-child(2) { display: none; } /* 라인1 제거 */
    > *:nth-child(3) { grid-column: 2; grid-row: 1; } /* 오존 */
    > *:nth-child(4) { display: none; } /* 라인2 제거 */
    > *:nth-child(5) { grid-column: 3; grid-row: 1; } /* 습도 */
    > *:nth-child(6) { display: none; } /* 라인3 제거 */
    > *:nth-child(7) { grid-column: 1; grid-row: 2; } /* 미세먼지 */
    > *:nth-child(8) { display: none; } /* 라인4 제거 */
    > *:nth-child(9) { grid-column: 2; grid-row: 2; } /* VOC */
    > *:nth-child(10) { display: none; } /* 라인5 제거 */
    > *:nth-child(11) { grid-column: 3; grid-row: 2; } /* 이산화탄소 */
  `
      : `
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;
    grid-template-rows: auto;
    place-items: stretch;

    /* 기본 1행 6열 배치 - 라인 없이 */
    > *:nth-child(1) { grid-column: 1; grid-row: 1; } /* 온도 */
    > *:nth-child(2) { display: none; } /* 라인1 제거 */
    > *:nth-child(3) { grid-column: 2; grid-row: 1; } /* 오존 */
    > *:nth-child(4) { display: none; } /* 라인2 제거 */
    > *:nth-child(5) { grid-column: 3; grid-row: 1; } /* 습도 */
    > *:nth-child(6) { display: none; } /* 라인3 제거 */
    > *:nth-child(7) { grid-column: 4; grid-row: 1; } /* 미세먼지 */
    > *:nth-child(8) { display: none; } /* 라인4 제거 */
    > *:nth-child(9) { grid-column: 5; grid-row: 1; } /* VOC */
    > *:nth-child(10) { display: none; } /* 라인5 제거 */
    > *:nth-child(11) { grid-column: 6; grid-row: 1; } /* 이산화탄소 */
  `}
`;

const SensorItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: auto;
  padding: 4px;
  white-space: nowrap;
  text-align: center;
  box-sizing: border-box;
  min-height: 0;

  > * {
    align-self: center;
    text-align: center;
  }
`;

const CenterPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  background: linear-gradient(
    136deg,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient01", themeMode)}
      0%,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient02", themeMode)}
      50%,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient03", themeMode)}
      100%
  );
  border-radius: 20px;
  margin: 10px;
  outline: 3px
    ${({ themeMode }) => getThemeColor("WeatherPanelOutline", themeMode)} solid; // color name = WeatherPanelOutline
  outline-offset: -3px;
  backdrop-filter: blur(12px);
  /* 기본 패딩을 가로 모드 기준으로 줄임 (경량화) */
  padding: 24px; /* 줄어든 기본 패딩 */

  min-height: 0;
  overflow: hidden;

  flex-shrink: 1; /* shrink 허용 */

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    border-radius: 20px;
    margin: 5px;
    padding: 15px;
  `}
`;

const WeatherIcon = styled.img`
  width: 64px;
  height: 64px;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    width: 32px;
    height: 32px;
  `}
`;

const WeatherIconContainer = styled.div`
  width: 64px;
  height: 64px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  /* 아이콘이 컨테이너에 딱 맞게 조정 */
  > * {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    width: 32px;
    height: 32px;
  `}
`;

const WeatherLabel = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor(
      "WeatherLabelColor",
      themeMode
    )}; // color name = WeatherLabelColor
  font-size: calc(1.6 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    --font-scaler: 10px;
  `}
`;

const WeatherValueNumber = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.5 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    --font-scaler: 10px;
  `}
`;

const WeatherValueUnit = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(1.6 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 500;
  line-height: 1.2;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    --font-scaler: 10px;
  `}
`;

const BottomMessage = styled.div`
  padding: 15px;
  background: linear-gradient(
    136deg,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient01", themeMode)}
      0%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient02", themeMode)}
      50%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient03", themeMode)}
      100%
  );
  border-radius: 20px; /* MainPanel, WeatherPanel과 동일한 radius */
  outline: 3px
    ${({ themeMode }) => getThemeColor("MainPanelOutline", themeMode)} solid; // color name = MainPanelOutline
  outline-offset: -3px;
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0; /* 크기가 줄어들지 않도록 */

  margin: 10px; /* MainPanel, WeatherPanel과 동일한 margin으로 좌우 길이 맞춤 */

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    padding: 8px; /* VH 기준으로 작게 */
    border-radius: 20px; /* MainPanel, WeatherPanel과 동일한 비율로 변경 */
    gap: 8px; /* VH 기준으로 작게 */
    margin: 5px; /* VH 기준으로 작게 */
  `}
`;

const MessageIcon = styled.div`
  width: 28.8px;
  height: 28.8px;
  background: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  border-radius: 50%;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    width: 15px; /* VH 기준으로 작게 */
    height: 15px;
  `}
`;

const MessageTextContainer = styled.div`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
`;

const MessageText = styled.div`
  font-size: calc(1.8 * var(--font-scaler));
  font-weight: 600;
  white-space: nowrap;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    --font-scaler: 10px; /* VH 기준으로 작게 */
  `}

  ${({ isOverflowing }) =>
    isOverflowing
      ? css`
          display: inline-block;
          will-change: transform;
          animation: ${marqueeAnimation} 15s linear infinite;
          position: relative;

          &::after {
            content: attr(data-text);
            position: absolute;
            left: 100%;
            top: 0;
            padding-left: 4em; /* Gap between repeated text */
            white-space: nowrap;
          }
        `
      : css`
          overflow: visible;
        `}
`;

const StyledLine653 = styled.div`
  width: 5px;
  height: 117px; /* 센서 높이의 33% */
  opacity: 0.6;
  background: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  align-self: center;

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    width: 4px !important; 
    height: 90px !important;
  `
      : `
     /* landscape default */
  `}
`;

const SensorSeparator = ({ themeMode, orientation }) => {
  return <StyledLine653 themeMode={themeMode} orientation={orientation} />;
};

// AtomsGauge를 SensorItem 크기에 맞게 조절하는 래퍼
const GaugeWrapper = styled.div`
  width: 80%;
  height: 80%;
  max-width: 104px;
  max-height: 104px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* AtomsGauge 컴포넌트 전체 크기 조절 */
  > * {
    transform: scale(0.9);
    transform-origin: center center;
  }

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    width: 100% !important;
    height: 100% !important;
    max-width: 160px !important;
    max-height: 180px !important;
    > * {
      transform: scale(1.2) !important;
    }
  `
      : `
    /* landscape default */
  `}
`;

// 새로운 날씨 컨텐츠 래퍼 - 가로/세로 모드에 따라 다른 배치
const CenterPanelContentWrapper = styled.div`
  display: flex;
  flex-direction: row; /* 기본: 가로 배치 */
  flex: 1;
  align-items: stretch;

  /* 가로 모드: 모든 요소를 균등 분배 */
  ${({ orientation }) =>
    orientation === "landscape"
      ? `
    gap: 0;
    justify-content: space-between;
  `
      : `
    /* 세로 모드: 중앙 배치 */
    gap: 30px;
    justify-content: center;
  `}

  align-items: center;
  width: 100%;
  min-height: 0;
  flex-shrink: 1;
`;

const DateTimeSection = styled.div`
  flex-direction: column;
  display: flex;

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
        /* 세로 모드: 제목 아래에 배치, 오른쪽 정렬 */
        position: relative; /* Make it flow normally */
        align-items: flex-end; /* Align content to the right */
        padding: 0;
        margin: 0;
        margin-left: auto; /* Push to the right in portrait mode */
      `
      : `
        /* 가로 모드: 타이틀 아래 배치, 중앙 정렬 */
        position: relative;
        align-items: center;
        justify-content: center;
        padding: 0; /* 패딩 제거 */
        margin-top: 0px;
  `}

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    gap: 2px;
  `}
`;

const DateContainer = styled.div`
  flex-direction: row; /* column → row로 변경 */
  justify-content: space-between; /* center → space-between으로 변경 */
  align-items: center; /* flex-end → center로 변경 */
  display: flex;
  gap: 0; /* 간격 완전 제거 */
  width: 100%; /* 전체 너비 사용 */
`;

const DateRow = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 13.44px;
  display: flex;
`;

const DateText = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.05 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    font-size: calc(2.1 * var(--font-scaler));
  `}
`;

const TimeText = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.05 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  margin-left: 20px;
  text-align: center;
  flex-shrink: 0;

  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    font-size: calc(2.1 * var(--font-scaler));
    margin-left: 10px;
  `}
`;
